// src/pages/Admin/AdminOrderDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ordersAPI } from '../../api/orders.js';
import { useToast } from '../../hooks/useToast.js';
import { Button } from '../../components/ui/Button/Button.jsx';
import { Loader } from '../../components/ui/Loader/Loader.jsx';
import { formatDate, formatPrice } from '../../utils/formatters.js';
import { ORDER_STATUS_LABELS, PAYMENT_STATUS_LABELS } from '../../utils/constants.js';

export const AdminOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const [order, setOrder] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [statusNote, setStatusNote] = useState('');

  useEffect(() => {
    loadOrder();
  }, [id]);

  const loadOrder = async () => {
    setIsLoading(true);
    try {
      const response = await ordersAPI.getById(id);
      setOrder(response.data.data);
    } catch (err) {
      error('Не удалось загрузить заказ');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async (newStatus) => {
    if (!window.confirm(`Изменить статус заказа на "${ORDER_STATUS_LABELS[newStatus]}"?`)) return;

    setIsUpdating(true);
    try {
      await ordersAPI.updateStatus(id, {
        status: newStatus,
        note: statusNote || `Статус изменен на ${ORDER_STATUS_LABELS[newStatus]}`,
      });
      success('Статус заказа обновлен');
      setStatusNote('');
      loadOrder();
    } catch (err) {
      error('Не удалось обновить статус');
    } finally {
      setIsUpdating(false);
    }
  };

  const handlePaymentUpdate = async (paymentStatus) => {
    setIsUpdating(true);
    try {
      await ordersAPI.updatePayment(id, {
        payment_status: paymentStatus,
        paid_at: paymentStatus === 'paid' ? new Date().toISOString() : null,
      });
      success('Статус оплаты обновлен');
      loadOrder();
    } catch (err) {
      error('Не удалось обновить статус оплаты');
    } finally {
      setIsUpdating(false);
    }
  };

  if (isLoading) {
    return <Loader text="Загрузка заказа..." />;
  }

  if (!order) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0' }}>
        <h2>Заказ не найден</h2>
        <Link to="/admin/orders">
          <Button style={{ marginTop: '16px' }}>Вернуться к заказам</Button>
        </Link>
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <div>
          <Link to="/admin/orders" style={{ color: 'rgb(var(--c-text-secondary))', textDecoration: 'none', fontSize: '14px' }}>
            ← Назад к заказам
          </Link>
          <h1 style={{
            fontSize: '24px',
            fontWeight: 700,
            color: 'rgb(var(--c-text))',
            marginTop: '8px',
          }}>
            Заказ #{order.order_number}
          </h1>
          <div style={{ fontSize: '14px', color: 'rgb(var(--c-text-secondary))' }}>
            Создан: {formatDate(order.createdAt)}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <span style={{
            padding: '6px 16px',
            borderRadius: '100px',
            fontSize: '14px',
            fontWeight: 500,
            backgroundColor: order.status === 'completed' ? '#D4EDDA' :
                          order.status === 'cancelled' ? '#F8D7DA' :
                          order.status === 'processing' ? '#CCE5FF' :
                          order.status === 'shipped' ? '#CFF3E8' :
                          '#FFF3CD',
            color: order.status === 'completed' ? '#155724' :
                   order.status === 'cancelled' ? '#721C24' :
                   order.status === 'processing' ? '#004085' :
                   order.status === 'shipped' ? '#0B5345' :
                   '#856404',
          }}>
            {ORDER_STATUS_LABELS[order.status] || order.status}
          </span>
          <span style={{
            padding: '6px 16px',
            borderRadius: '100px',
            fontSize: '14px',
            fontWeight: 500,
            backgroundColor: order.payment_status === 'paid' ? '#D4EDDA' :
                          order.payment_status === 'failed' ? '#F8D7DA' :
                          '#FFF3CD',
            color: order.payment_status === 'paid' ? '#155724' :
                   order.payment_status === 'failed' ? '#721C24' :
                   '#856404',
          }}>
            {PAYMENT_STATUS_LABELS[order.payment_status] || order.payment_status}
          </span>
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: '24px',
      }}>
        {/* Левая колонка */}
        <div>
          {/* Товары */}
          <div style={{
            backgroundColor: 'rgb(var(--c-surface))',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
              Товары
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgb(var(--c-border))' }}>
                    <th style={{ padding: '8px 12px', textAlign: 'left', color: 'rgb(var(--c-text-secondary))', fontWeight: 500 }}>Товар</th>
                    <th style={{ padding: '8px 12px', textAlign: 'center', color: 'rgb(var(--c-text-secondary))', fontWeight: 500 }}>Кол-во</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', color: 'rgb(var(--c-text-secondary))', fontWeight: 500 }}>Цена</th>
                    <th style={{ padding: '8px 12px', textAlign: 'right', color: 'rgb(var(--c-text-secondary))', fontWeight: 500 }}>Сумма</th>
                  </tr>
                </thead>
                <tbody>
                  {order.OrderItems?.map((item) => (
                    <tr key={item.id} style={{ borderBottom: '1px solid rgb(var(--c-surface-2))' }}>
                      <td style={{ padding: '8px 12px' }}>
                        <div style={{ fontWeight: 500 }}>{item.product_name}</div>
                        <div style={{ fontSize: '12px', color: 'rgb(var(--c-text-secondary))' }}>Арт: {item.product_sku}</div>
                        {item.calculator_data && Object.keys(item.calculator_data).length > 0 && (
                          <div style={{ fontSize: '11px', color: 'rgb(var(--c-text-secondary))' }}>
                            {Object.entries(item.calculator_data).map(([key, val]) => (
                              <span key={key} style={{ marginRight: '8px' }}>{key}: {val}</span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'center' }}>{item.quantity}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>{formatPrice(item.unit_price)}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600 }}>
                        {formatPrice(item.total_price)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid rgb(var(--c-border))' }}>
                    <td colSpan="3" style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>
                      Итого:
                    </td>
                    <td style={{ padding: '12px', textAlign: 'right', fontSize: '18px', fontWeight: 700, color: 'rgb(var(--c-accent))' }}>
                      {formatPrice(order.total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* История статусов */}
          <div style={{
            backgroundColor: 'rgb(var(--c-surface))',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
              История статусов
            </h3>
            <div style={{ position: 'relative', paddingLeft: '24px' }}>
              {order.OrderStatusHistories?.map((history, index) => (
                <div key={index} style={{ position: 'relative', paddingBottom: '16px' }}>
                  {index < order.OrderStatusHistories.length - 1 && (
                    <div style={{
                      position: 'absolute',
                      left: '-16px',
                      top: '24px',
                      width: '2px',
                      height: 'calc(100% - 16px)',
                      backgroundColor: 'rgb(var(--c-border))',
                    }} />
                  )}
                  <div style={{
                    position: 'absolute',
                    left: '-20px',
                    top: '4px',
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    backgroundColor: 'rgb(var(--c-accent))',
                  }} />
                  <div style={{ fontWeight: 500, color: 'rgb(var(--c-text))' }}>
                    {ORDER_STATUS_LABELS[history.status] || history.status}
                  </div>
                  <div style={{ fontSize: '13px', color: 'rgb(var(--c-text-secondary))' }}>
                    {formatDate(history.createdAt)}
                    {history.User && ` — ${history.User.first_name}`}
                  </div>
                  {history.note && (
                    <div style={{ fontSize: '13px', color: 'rgb(var(--c-text-secondary))', marginTop: '4px', fontStyle: 'italic' }}>
                      {history.note}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Правая колонка */}
        <div>
          {/* Информация о клиенте */}
          <div style={{
            backgroundColor: 'rgb(var(--c-surface))',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>
              Информация о клиенте
            </h3>
            {order.User ? (
              <>
                <div style={{ fontWeight: 500 }}>
                  {order.User.first_name} {order.User.last_name || ''}
                </div>
                <div style={{ fontSize: '14px', color: 'rgb(var(--c-text-secondary))' }}>{order.User.email}</div>
                {order.User.phone && (
                  <div style={{ fontSize: '14px', color: 'rgb(var(--c-text-secondary))' }}>{order.User.phone}</div>
                )}
              </>
            ) : (
              <>
                <div style={{ fontWeight: 500 }}>{order.guest_email || 'Гость'}</div>
                {order.guest_phone && (
                  <div style={{ fontSize: '14px', color: 'rgb(var(--c-text-secondary))' }}>{order.guest_phone}</div>
                )}
                <div style={{ fontSize: '12px', color: 'rgb(var(--c-text-secondary))' }}>Гостевой заказ</div>
              </>
            )}
          </div>

          {/* Адрес доставки */}
          {order.delivery_address && (
            <div style={{
              backgroundColor: 'rgb(var(--c-surface))',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>
                Адрес доставки
              </h3>
              <div style={{ fontSize: '14px', color: 'rgb(var(--c-text-secondary))' }}>{order.delivery_address}</div>
              {order.delivery_method && (
                <div style={{ fontSize: '14px', color: 'rgb(var(--c-text-secondary))', marginTop: '4px' }}>
                  Способ: {order.delivery_method}
                </div>
              )}
            </div>
          )}

          {/* Детали оплаты */}
          <div style={{
            backgroundColor: 'rgb(var(--c-surface))',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>
              Оплата
            </h3>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ color: 'rgb(var(--c-text-secondary))' }}>Метод:</span>
              <span>{order.payment_method || 'Не указан'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
              <span style={{ color: 'rgb(var(--c-text-secondary))' }}>Статус:</span>
              <span style={{
                fontWeight: 500,
                color: order.payment_status === 'paid' ? '#28A745' :
                       order.payment_status === 'failed' ? '#DC3545' :
                       '#FFC107',
              }}>
                {PAYMENT_STATUS_LABELS[order.payment_status] || order.payment_status}
              </span>
            </div>
            {order.paid_at && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                <span style={{ color: 'rgb(var(--c-text-secondary))' }}>Оплачен:</span>
                <span>{formatDate(order.paid_at)}</span>
              </div>
            )}
          </div>

          {/* Комментарий клиента */}
          {order.customer_notes && (
            <div style={{
              backgroundColor: 'rgb(var(--c-surface))',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '16px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            }}>
              <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '12px' }}>
                Комментарий клиента
              </h3>
              <div style={{ fontSize: '14px', color: 'rgb(var(--c-text-secondary))' }}>{order.customer_notes}</div>
            </div>
          )}

          {/* Управление статусом */}
          <div style={{
            backgroundColor: 'rgb(var(--c-surface))',
            borderRadius: '12px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
            position: 'sticky',
            top: '20px',
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '16px' }}>
              Управление заказом
            </h3>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>
                Изменить статус
              </label>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {Object.keys(ORDER_STATUS_LABELS).map((status) => (
                  <button
                    key={status}
                    onClick={() => handleStatusUpdate(status)}
                    disabled={isUpdating || status === order.status}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '6px',
                      border: status === order.status ? '2px solid rgb(var(--c-accent))' : '1px solid rgb(var(--c-border))',
                      backgroundColor: status === order.status ? '#E8EEF5' : 'transparent',
                      color: status === order.status ? '#205CA0' : '#1A1A1A',
                      cursor: status === order.status || isUpdating ? 'default' : 'pointer',
                      fontSize: '12px',
                      opacity: status === order.status || isUpdating ? 0.6 : 1,
                    }}
                  >
                    {ORDER_STATUS_LABELS[status]}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '12px' }}>
              <Input
                label="Заметка к изменению статуса"
                value={statusNote}
                onChange={(e) => setStatusNote(e.target.value)}
                placeholder="Причина изменения..."
              />
            </div>

            <div style={{ marginBottom: '12px' }}>
              <label style={{ fontSize: '14px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>
                Изменить статус оплаты
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {['pending', 'paid', 'failed', 'refunded'].map((status) => (
                  <button
                    key={status}
                    onClick={() => handlePaymentUpdate(status)}
                    disabled={isUpdating || status === order.payment_status}
                    style={{
                      padding: '4px 12px',
                      borderRadius: '6px',
                      border: status === order.payment_status ? '2px solid rgb(var(--c-accent))' : '1px solid rgb(var(--c-border))',
                      backgroundColor: status === order.payment_status ? '#E8EEF5' : 'transparent',
                      color: status === order.payment_status ? '#205CA0' : '#1A1A1A',
                      cursor: status === order.payment_status || isUpdating ? 'default' : 'pointer',
                      fontSize: '12px',
                      opacity: status === order.payment_status || isUpdating ? 0.6 : 1,
                    }}
                  >
                    {PAYMENT_STATUS_LABELS[status]}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
              <Button
                variant="secondary"
                onClick={() => navigate('/admin/orders')}
                style={{ flex: 1 }}
              >
                ← Назад
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};